/**
 * IXD Preview 비교 결과를 새 탭에서 렌더하는 라우트(/ixd-preview-compare/view).
 *
 * 소스 spec(업로드 File 포함)은 URL/localStorage 로 직렬화할 수 없어, opener 와의
 * postMessage 핸드셰이크로 전달받는다(structured clone 이 File 을 그대로 복제).
 */

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";

import { useIxdPreviewCompareStream } from "@/hooks/ixd-preview-compare/use-ixd-preview-compare-stream";
import { useIxdPreviewCompareStore } from "@/stores/ixd-preview-compare";
import { IxdPreviewCompareStoreProvider } from "./compare-store-provider";
import { IxdPreviewCompareResultView } from "./result-view";
import { IxdIxdDownloadButton } from "@/components/ixd-ixd-compare/export/download-button";
import {
  RESULT_TAB_READY,
  RESULT_TAB_SPECS,
  type ResultTabSpecsMessage,
} from "@/utils/ixd-preview-compare/result-tab";

// DSD-DSD 대사의 로딩/메시지 화면과 동일(전체화면 bg-white + 스피너).
const LoadingScreen = ({ message }: { message: string }) => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="size-8 animate-spin text-label-assistive" />
      <p className="body-1-normal-medium text-label-assistive">{message}</p>
    </div>
  </div>
);

const MessageScreen = ({ message }: { message: string }) => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <p className="body-1-normal-medium text-label-assistive">{message}</p>
  </div>
);

const IxdPreviewCompareViewInner = () => {
  const { run } = useIxdPreviewCompareStream();
  const status = useIxdPreviewCompareStore((s) => s.status);
  const error = useIxdPreviewCompareStore((s) => s.error);
  const [received, setReceived] = useState(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as Partial<ResultTabSpecsMessage> | undefined;
      if (data?.type !== RESULT_TAB_SPECS || !data.src || !data.trg) return;
      setReceived(true);
      window.removeEventListener("message", onMessage);
      void run(data.src, data.trg);
    };
    window.addEventListener("message", onMessage);
    if (window.opener) {
      window.opener.postMessage({ type: RESULT_TAB_READY }, window.location.origin);
    }
    return () => window.removeEventListener("message", onMessage);
  }, [run]);

  if (!received) {
    return window.opener ? (
      <LoadingScreen message="비교 결과를 연결하는 중..." />
    ) : (
      <MessageScreen message="입력 화면에서 다시 시도해주세요." />
    );
  }

  if (status === "error") {
    return <MessageScreen message={error ?? "오류가 발생했습니다."} />;
  }

  // 스트리밍이 모두 끝난 뒤에 결과를 한 번에 보여준다. 그 전까지는 로딩.
  if (status !== "done") {
    return <LoadingScreen message="IXD 비교 진행 중..." />;
  }

  return (
    <IxdPreviewCompareResultView headerAction={<IxdIxdDownloadButton />} />
  );
};

export const IxdPreviewCompareViewPage = () => (
  <IxdPreviewCompareStoreProvider>
    <Sentry.ErrorBoundary
      fallback={
        <MessageScreen message="비교 결과를 표시하는 중 오류가 발생했습니다." />
      }
    >
      <IxdPreviewCompareViewInner />
    </Sentry.ErrorBoundary>
  </IxdPreviewCompareStoreProvider>
);

export default IxdPreviewCompareViewPage;
